/**
 * AssignFirmUserResolution - Row for managing resolution assignment and status.
 *
 * Left side: "Assign for Resolution" label + firm user dropdown + action buttons
 * Right side: actionsSlot (Download, Mark as Unread, Sidebar, etc.)
 *
 * Button states:
 * - No saved assignees → [Cancel (disabled)] [Assign (disabled)] — enabled on any dropdown change
 * - Has saved assignees → [Cancel (disabled)] [Update (disabled)] [Mark as Resolved] — Cancel & Update
 *   enabled only on dropdown change; Mark as Resolved always clickable (shows confirmation first)
 *
 * On Assign/Update: checks if any selected user is not yet a conversation participant.
 * If conflicts exist, a confirmation dialog is shown before saving.
 */

import React, { useEffect, useCallback, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';

import { useUpdateMessageThread, useInviteUsers } from '../../hooks/useMessageMutations';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import CaseUserSelect from 'components/UI/CaseUserSelect/CaseUserSelect';
import GlobalTooltip from 'components/GlobalTooltip';
import { toFirmUserOptions } from 'hooks/queries/useFirmUsersDropdown';
import { useMessageContext } from '../../context/MessageContext';
import { generateFullName } from 'utils/generateFullNameUtils';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns selected assignees who are NOT already participants in the thread.
 * assign_to items: { value: firm_user_id, label, ... }
 * firmUsers items: { firm_user_id, ... }
 */
function findNonParticipantAssignees(assignTo, firmUsers) {
  return (assignTo || []).filter((assignee) => !(firmUsers || []).some((fu) => fu.firm_user_id === assignee.value));
}

// ── Component ────────────────────────────────────────────────────────────────

function AssignFirmUserResolution({ isFromLead, messageDetails, hasPermission, actionsSlot }) {
  const { addToast } = useToast();
  const updateThreadMutation = useUpdateMessageThread();
  const inviteUsersMutation = useInviteUsers();
  const { getCaseDetails, isFromCase } = useMessageContext();

  // Tracks which action triggered the mutation so spinners don't bleed across buttons
  const [activeAction, setActiveAction] = useState(null); // 'assign' | 'resolve'

  // Conflict dialog state (non-participant assignees)
  const [pendingData, setPendingData] = useState(null);
  const [conflictAssignees, setConflictAssignees] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: { assign_to: [] },
  });

  // Use reset (not setValue) so isDirty accurately tracks changes from saved state
  useEffect(() => {
    if (messageDetails) {
      reset({ assign_to: toFirmUserOptions(messageDetails?.assign_to || []) });
    }
  }, [messageDetails, reset]);

  const hasExistingAssignees = (messageDetails?.assign_to?.length ?? 0) > 0;
  const isMutating = updateThreadMutation.isPending || inviteUsersMutation.isPending;
  const assignTo = useWatch({ control, name: 'assign_to' }) || [];

  // ── Core mutation ──────────────────────────────────────────────────────────

  const runMutation = useCallback(
    (data, extraFields = {}, toastMessages = {}) => {
      const {
        successMessage = toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_SUCCESS,
        errorMessage = toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_FAILED,
      } = toastMessages;

      const sendData = {
        sk: `message#${messageDetails?.message_thread_id}`,
        message_thread_id: messageDetails?.message_thread_id,
        case_id: messageDetails?.case?.case_id,
        case_name: messageDetails?.case?.case_name,
        is_new: false,
        is_read: true,
        subject: messageDetails?.subject,
        assign_to: data.assign_to || [],
        resolution_status: messageDetails?.resolution_status || false,
        resolved_status: messageDetails?.resolved_status || false,
        ...extraFields,
      };

      return updateThreadMutation.mutate(
        { data: sendData, isLead: isFromLead },
        {
          onSuccess: () => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, successMessage);
            // Reset baseline so isDirty returns to false
            reset({ assign_to: toFirmUserOptions(data?.assign_to || []) });
            setActiveAction(null);
            if ((isFromLead || isFromCase) && typeof getCaseDetails === 'function') {
              getCaseDetails();
            }
          },
          onError: () => {
            setActiveAction(null);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMessage);
          },
        }
      );
    },
    [messageDetails, isFromLead, reset, isFromCase]
  );

  // ── Assign / Update submit ─────────────────────────────────────────────────

  const onSubmit = useCallback(
    (data) => {
      const conflicts = findNonParticipantAssignees(data.assign_to, messageDetails?.firm_users);
      if (conflicts.length > 0) {
        setPendingData(data);
        setConflictAssignees(conflicts);
        setShowConflictDialog(true);
        return;
      }
      setActiveAction('assign');
      runMutation(data);
    },
    [messageDetails?.firm_users, runMutation]
  );

  const handleConflictConfirm = useCallback(() => {
    if (!pendingData) return;
    setShowConflictDialog(false);

    inviteUsersMutation.mutate(
      {
        message_thread_id: messageDetails?.message_thread_id,
        sk: `message#${messageDetails?.message_thread_id}`,
        isLead: isFromLead || messageDetails?.is_lead,
        data: {
          invited_user: conflictAssignees.map((u) => ({
            calendar_color: u.calendar_color,
            is_confirmed: u.is_confirmed,
            firm_user_id: u.firm_user_id,
            profile_image: u.profile_image,
            client_cognito_username: u.client_cognito_username,
            middle_name: u.middle_name,
            last_name: u.last_name,
            pk: u.pk,
            status: u.status,
            email: u.email,
            first_name: u.first_name,
            access_level: u.access_level,
            recipient_name: generateFullName(u),
            recipient_id: u.client_cognito_username,
          })),
          invited_group: [],
        },
      },
      {
        onSuccess: () => {
          setActiveAction('assign');
          runMutation(pendingData);
          setPendingData(null);
          setConflictAssignees([]);
        },
        onError: () => {
          addToast(
            false,
            toastConstant.toasterType.ERROR,
            toastConstant.api.FAILED,
            toastConstant.message.SECURE_MESSAGE_THREAD_UPDATED_FAILED
          );
          setPendingData(null);
          setConflictAssignees([]);
        },
      }
    );
  }, [pendingData, conflictAssignees, messageDetails, isFromLead, inviteUsersMutation, runMutation, addToast]);

  const handleConflictCancel = useCallback(() => {
    setShowConflictDialog(false);
    setPendingData(null);
    setConflictAssignees([]);
  }, []);

  // ── Mark as Resolved ───────────────────────────────────────────────────────

  const handleMarkResolvedClick = useCallback(() => {
    confirmDialog({
      header: 'Mark as Resolved',
      message: 'Are you sure you want to mark this thread as resolved? This action will close the resolution.',
      icon: 'pi pi-check-circle',
      acceptLabel: 'Mark as Resolved',
      rejectLabel: 'Cancel',
      acceptClassName: 'p-button-primary p-button-secondary',
      rejectClassName: 'p-button-secondary outline',
      style: { width: '40vw', minWidth: '360px' },
      accept: () => {
        setActiveAction('resolve');
        runMutation(
          { assign_to: getValues('assign_to') },
          { resolution_status: true, resolved_status: true },
          {
            successMessage: toastConstant.message.SECURE_MESSAGE_MARK_AS_RESOLVED_SUCCESS,
            errorMessage: toastConstant.message.SECURE_MESSAGE_MARK_AS_RESOLVED_FAILED,
          }
        );
      },
    });
  }, [getValues, runMutation]);

  // ── Cancel / reset form ────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    reset({ assign_to: toFirmUserOptions(messageDetails?.assign_to || []) });
  }, [reset, messageDetails]);

  if (!messageDetails) return null;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap  gap-2 mb-2">
        <form onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center flex-wrap gap-2 px-3 py-2 filter-wrap">
          <label className="p-medium text-muted mb-0">Assign for Resolution</label>

          <div className="d-flex flex-column filter-wraper">
            <Controller
              name="assign_to"
              control={control}
              render={({ field }) => {
                const assignedNames = (field.value || []).map((u) => u.label).join(', ');
                return (
                  <GlobalTooltip tooltip={assignedNames || undefined} position="top" showDelay={300}>
                    <CaseUserSelect
                      id={field.name}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      caseId={messageDetails?.case?.case_id}
                      placeholder="Select a Firm User"
                      isMulti
                      isClearable={false}
                      className="input-width"
                      isSearchable
                      isDisabled={!hasPermission}
                    />
                  </GlobalTooltip>
                );
              }}
            />
            {errors?.assign_to && <InputErrorMessage className="mt-1">{errors.assign_to.message}</InputErrorMessage>}
          </div>

          {hasPermission && (
            <div className="d-flex align-items-center gap-2">
              {isDirty ? (
                <>
                  <Button
                    label="Cancel"
                    className="btn-outline p-button-secondary outline py-1 px-3"
                    type="button"
                    style={{ overflow: 'inherit' }}
                    onClick={handleCancel}
                    disabled={isMutating}
                  />
                  <Button
                    label="Assign"
                    className="btn-outline p-button-primary p-button-secondary py-1 px-3"
                    type="submit"
                    loading={activeAction === 'assign' && isMutating}
                    disabled={isMutating || assignTo.length === 0}
                  />
                </>
              ) : (
                hasExistingAssignees && (
                  <Button
                    label="Mark as Resolved"
                    icon="pi pi-check-circle"
                    className="p-button-secondary p-button-sm outline"
                    type="button"
                    onClick={handleMarkResolvedClick}
                    disabled={isMutating}
                    loading={activeAction === 'resolve' && isMutating}
                  />
                )
              )}
            </div>
          )}
        </form>

        {actionsSlot && <div className="d-flex align-items-center gap-2 flex-wrap">{actionsSlot}</div>}
      </div>

      {/* Non-participant conflict dialog */}
      <Dialog
        header="User Not in Conversation"
        visible={showConflictDialog}
        onHide={handleConflictCancel}
        style={{ width: '40vw', minWidth: '360px' }}
        className="common-popup-style"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-secondary outline" onClick={handleConflictCancel} />
            <Button
              label="Yes, Assign Anyway"
              icon="pi pi-check"
              className="p-button-secondary"
              onClick={handleConflictConfirm}
              loading={isMutating}
              disabled={isMutating}
            />
          </div>
        }
        modal
      >
        <p className="mb-2">
          The following user{conflictAssignees.length > 1 ? 's are' : ' is'} not currently a participant in this conversation:
        </p>
        <ul className="mb-3">
          {conflictAssignees.map((u, i) => (
            <li key={u.value || i}>{u.label}</li>
          ))}
        </ul>
        <p className="mb-0">Do you still want to assign {conflictAssignees.length > 1 ? 'them' : 'this user'} for resolution?</p>
      </Dialog>
    </>
  );
}

AssignFirmUserResolution.propTypes = {
  isFromLead: PropTypes.bool,
  hasPermission: PropTypes.bool,
  messageDetails: PropTypes.shape({
    sk: PropTypes.string,
    case_id: PropTypes.string,
    is_new: PropTypes.bool,
    is_read: PropTypes.bool,
    case_name: PropTypes.string,
    subject: PropTypes.string,
    assign_to: PropTypes.array,
    firm_users: PropTypes.arrayOf(PropTypes.object),
    resolution_status: PropTypes.bool,
    resolved_status: PropTypes.bool,
  }),
  actionsSlot: PropTypes.node,
};

AssignFirmUserResolution.defaultProps = {
  isFromLead: false,
  hasPermission: false,
  messageDetails: null,
  actionsSlot: null,
};

export default memo(AssignFirmUserResolution);
